import { useState } from 'react'
import { Link } from 'react-router-dom'
import { SiteFooter, SiteHeader } from './SiteChrome'
import { useScrollToAnchor } from './useScrollToAnchor'
import { supportEmail, supportPhone, supportPhoneHref } from './siteMenu'

const inputClass =
  'w-full rounded-[8px] border border-white/20 bg-neutral-950 px-4 py-3 text-sm text-white placeholder:text-white/35 focus:border-aan-bright focus:outline-none focus:ring-1 focus:ring-aan-bright'

const enquiryTopics = [
  'General enquiry',
  'AAN Productions — project submission',
  'AAN EDUC8 — training and masterclasses',
  'TalentAfrica.net — talent registration',
  'Adinkra Experience — selling on the marketplace',
  'AECinemax — film submission',
  'Partnership',
  'Volunteering',
  'Membership',
  'Ticketing support',
]

export default function ContactPage() {
  useScrollToAnchor()
  const [form, setForm] = useState({ name: '', email: '', topic: enquiryTopics[0], message: '' })

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const subject = `${form.topic} — ${form.name}`
    const body = `Name: ${form.name}\nEmail: ${form.email}\nTopic: ${form.topic}\n\n${form.message}`
    window.location.href = `mailto:${supportEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
  }

  return (
    <main className="aan-site min-h-screen bg-black text-white">
      <SiteHeader />

      <section className="border-b border-white/10 bg-black px-6 pb-14 pt-12 sm:pb-16 sm:pt-16">
        <div className="mx-auto max-w-[1080px]">
          <nav aria-label="Breadcrumb" className="font-display text-[11px] font-black uppercase tracking-[0.24em] text-white/45">
            <Link className="transition hover:text-aan-bright" to="/">
              Home
            </Link>
            <span className="px-2">/</span>
            <Link className="transition hover:text-aan-bright" to="/about">
              About AAN
            </Link>
            <span className="px-2">/</span>
            <span className="text-aan-bright">Contact us</span>
          </nav>
          <h1 className="mt-6 font-display text-4xl font-black uppercase leading-none text-white sm:text-5xl lg:text-6xl">
            Contact us
          </h1>
          <p className="mt-7 max-w-3xl text-lg leading-8 text-white/75">
            Whether you have a project, a question about a programme, or you want to work with the network, the team
            would like to hear from you.
          </p>
        </div>
      </section>

      <div className="mx-auto grid max-w-[1080px] gap-10 px-6 py-14 sm:py-16 lg:grid-cols-[1.15fr_0.85fr] lg:py-20">
        <div className="rounded-[12px] border border-white/15 bg-neutral-950 p-7 sm:p-9">
          <h2 className="font-display text-2xl font-black uppercase leading-none text-white sm:text-3xl">
            Send us a message
          </h2>
          <p className="mt-4 text-sm leading-7 text-white/70">
            Complete the form and it will open in your email app, addressed to the AAN team.
          </p>
          <form className="mt-7 space-y-5" onSubmit={handleSubmit}>
            <div>
              <label className="mb-2 block font-display text-[11px] font-black uppercase tracking-[0.18em] text-white/70" htmlFor="contact-name">
                Your name
              </label>
              <input
                className={inputClass}
                id="contact-name"
                onChange={(event) => setForm({ ...form, name: event.target.value })}
                placeholder="Full name"
                required
                type="text"
                value={form.name}
              />
            </div>
            <div>
              <label className="mb-2 block font-display text-[11px] font-black uppercase tracking-[0.18em] text-white/70" htmlFor="contact-email">
                Email address
              </label>
              <input
                className={inputClass}
                id="contact-email"
                onChange={(event) => setForm({ ...form, email: event.target.value })}
                placeholder="you@example.com"
                required
                type="email"
                value={form.email}
              />
            </div>
            <div>
              <label className="mb-2 block font-display text-[11px] font-black uppercase tracking-[0.18em] text-white/70" htmlFor="contact-topic">
                What is it about?
              </label>
              <select
                className={inputClass}
                id="contact-topic"
                onChange={(event) => setForm({ ...form, topic: event.target.value })}
                value={form.topic}
              >
                {enquiryTopics.map((topic) => (
                  <option key={topic} value={topic}>
                    {topic}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-2 block font-display text-[11px] font-black uppercase tracking-[0.18em] text-white/70" htmlFor="contact-message">
                Message
              </label>
              <textarea
                className={inputClass}
                id="contact-message"
                onChange={(event) => setForm({ ...form, message: event.target.value })}
                placeholder="Tell us more about your enquiry"
                required
                rows={6}
                value={form.message}
              />
            </div>
            <button
              className="inline-flex min-h-12 w-full items-center justify-center rounded-full bg-aan-bright px-7 font-display text-xs font-black uppercase tracking-[0.14em] text-black transition hover:bg-white"
              type="submit"
            >
              Send message
            </button>
          </form>
        </div>

        <div className="space-y-5">
          <div className="rounded-[12px] border border-white/15 bg-neutral-950 p-7">
            <h3 className="font-display text-lg font-black uppercase text-aan-bright">Email us</h3>
            <p className="mt-2 text-sm leading-7 text-white/70">Our team is here to help.</p>
            <a className="mt-3 inline-block text-sm font-semibold text-white transition hover:text-aan-bright" href={`mailto:${supportEmail}`}>
              {supportEmail}
            </a>
          </div>
          <div className="rounded-[12px] border border-white/15 bg-neutral-950 p-7">
            <h3 className="font-display text-lg font-black uppercase text-aan-bright">Call us</h3>
            <p className="mt-2 text-sm leading-7 text-white/70">Monday to Friday, 8am to 6pm.</p>
            <a className="mt-3 inline-block text-sm font-semibold text-white transition hover:text-aan-bright" href={`tel:${supportPhoneHref}`}>
              {supportPhone}
            </a>
          </div>
          <div className="rounded-[12px] border border-white/15 bg-neutral-950 p-7">
            <h3 className="font-display text-lg font-black uppercase text-aan-bright">Visit us</h3>
            <p className="mt-2 text-sm leading-7 text-white/70">Kumasi, Ghana</p>
          </div>
          <div className="rounded-[12px] border border-aan-bright bg-aan-footer p-7 shadow-glow">
            <h3 className="font-display text-lg font-black uppercase text-white">Buying tickets?</h3>
            <p className="mt-2 text-sm leading-7 text-white/75">
              Screenings, performances and masterclasses are booked through AAN Events.
            </p>
            <Link
              className="mt-4 inline-flex min-h-11 items-center justify-center rounded-full bg-aan-bright px-6 font-display text-[11px] font-black uppercase tracking-[0.14em] text-black transition hover:bg-white"
              to="/events/upcoming"
            >
              Browse events
            </Link>
          </div>
        </div>
      </div>

      <SiteFooter />
    </main>
  )
}
